import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  imageAssetGuideIntro,
  imageAssetGuidePages,
} from "../../data/imageAssetGuide";

const relPath = (subfolder, file) =>
  subfolder.startsWith("public/")
    ? `${subfolder}/${file}`
    : subfolder
    ? `img/${subfolder}/${file}`
    : `img/${file}`;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

async function postImageUpload(file, subfolder, filename) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("subfolder", subfolder);
  fd.append("filename", filename);
  const res = await fetch("/api/image-guide/upload", {
    method: "POST",
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Upload failed (${res.status})`);
  }
  return data;
}

const ImageGuideArea = () => {
  const [copiedKey, setCopiedKey] = useState(null);
  const [filter, setFilter] = useState("");
  const [uploadServerOk, setUploadServerOk] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [uploadNote, setUploadNote] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetch("/api/image-guide/health")
      .then((r) => r.ok)
      .then((ok) => {
        if (!cancelled) setUploadServerOk(ok);
      })
      .catch(() => {
        if (!cancelled) setUploadServerOk(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredPages = useMemo(() => {
    if (!filter.trim()) return imageAssetGuidePages;
    const q = filter.toLowerCase();
    return imageAssetGuidePages
      .map((page) => {
        const assets = page.assets.filter(
          (a) =>
            a.file.toLowerCase().includes(q) ||
            a.subfolder.toLowerCase().includes(q) ||
            a.purpose.toLowerCase().includes(q)
        );
        return { ...page, assets };
      })
      .filter((p) => p.assets.length > 0);
  }, [filter]);

  return (
    <section id="image_guide_area" className="section_padding">
      <div className="container">
        <div className="row mb-4">
          <div className="col-lg-10 mx-auto">
            <div className="section_heading mb-3">
              <h3>Editor tools</h3>
              <h2>
                <span className="color_big_heading">Image</span> asset guide
              </h2>
            </div>
            <p className="mb-3">{imageAssetGuideIntro.title}</p>
            <p>
              <strong>Base folder:</strong>{" "}
              <code className="small">{imageAssetGuideIntro.basePath}</code>
            </p>
            <ul className="mb-4">
              {imageAssetGuideIntro.notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
            <div
              className={`alert small mb-4 ${
                uploadServerOk === true
                  ? "alert-success"
                  : uploadServerOk === false
                  ? "alert-warning"
                  : "alert-secondary"
              }`}
              role="status"
            >
              <strong>Upload from this page:</strong> in a second terminal run{" "}
              <code>npm run upload-server</code> inside the{" "}
              <code>Jagowelfare</code> folder, keep it running, then use{" "}
              <strong>Upload</strong> on each row. The file is saved under your
              project with the listed name (development only).
              {uploadServerOk === false && (
                <span className="d-block mt-2">
                  Upload server not detected — start it or uploads will fail.
                </span>
              )}
              {uploadServerOk === true && (
                <span className="d-block mt-2">Upload server is ready.</span>
              )}
            </div>
            <div className="form-group mb-4">
              <label className="small text-muted d-block mb-1">
                Filter by filename, folder, or note
              </label>
              <input
                type="search"
                className="form-control"
                placeholder="e.g. banner, volunteer, blog"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-lg-10 mx-auto">
            <div
              className="p-3 rounded"
              style={{ background: "var(--bg-color)", borderRadius: 8 }}
            >
              <h4 className="mb-3">Jump to page</h4>
              <div className="d-flex flex-wrap gap-2">
                {imageAssetGuidePages.map((p) => (
                  <a
                    key={p.id}
                    className="btn btn_sm btn_navber mb-1"
                    href={`#page-${p.id}`}
                    style={{ fontSize: 14 }}
                  >
                    {p.title}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {filteredPages.length === 0 && (
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <p className="text-muted">No rows match your filter.</p>
            </div>
          </div>
        )}

        {filteredPages.map((page) => (
          <div
            className="row mb-5"
            key={page.id}
            id={`page-${page.id}`}
            style={{ scrollMarginTop: 100 }}
          >
            <div className="col-lg-10 mx-auto">
              <h3 className="mb-1">{page.title}</h3>
              <p className="text-muted mb-2">
                Route:{" "}
                {page.route.startsWith("/") ? (
                  <Link to={page.route}>{page.route}</Link>
                ) : (
                  <span>{page.route}</span>
                )}
              </p>
              {page.intro && <p className="mb-3">{page.intro}</p>}
              <div className="table-responsive">
                <table className="table table-bordered table-striped align-middle small">
                  <thead className="table-light">
                    <tr>
                      <th scope="col">File name</th>
                      <th scope="col">Folder under assets/img</th>
                      <th scope="col">Recommended size</th>
                      <th scope="col">Format</th>
                      <th scope="col">Purpose</th>
                      <th scope="col">Copy path</th>
                      <th scope="col">Upload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.assets.map((a, idx) => {
                      const pathForCopy = a.subfolder.startsWith("public/")
                        ? `${a.subfolder}/${a.file}`
                        : `src/assets/${relPath(a.subfolder, a.file)}`;
                      const rowKey = `${page.id}-${idx}`;
                      const inputId = `ig-upload-${rowKey}`;
                      return (
                        <tr key={rowKey}>
                          <td>
                            <code>{a.file}</code>
                          </td>
                          <td>
                            <code>{a.subfolder || "(root)"}</code>
                          </td>
                          <td>{a.dims}</td>
                          <td>{a.format || "PNG"}</td>
                          <td>{a.purpose}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn_theme btn_sm"
                              style={{ padding: "4px 12px", fontSize: 12 }}
                              onClick={async () => {
                                await copyText(pathForCopy);
                                setCopiedKey(rowKey);
                                setTimeout(() => setCopiedKey(null), 1500);
                              }}
                            >
                              {copiedKey === rowKey ? "Copied" : "Copy"}
                            </button>
                          </td>
                          <td>
                            <input
                              id={inputId}
                              type="file"
                              accept="image/*"
                              className="d-none"
                              disabled={uploadingKey === rowKey}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                e.target.value = "";
                                if (!file) return;
                                setUploadingKey(rowKey);
                                setUploadNote((m) => ({
                                  ...m,
                                  [rowKey]: null,
                                }));
                                try {
                                  await postImageUpload(
                                    file,
                                    a.subfolder,
                                    a.file
                                  );
                                  setUploadNote((m) => ({
                                    ...m,
                                    [rowKey]: "Saved to project folder",
                                  }));
                                } catch (err) {
                                  setUploadNote((m) => ({
                                    ...m,
                                    [rowKey]:
                                      err instanceof Error
                                        ? err.message
                                        : "Upload failed",
                                  }));
                                } finally {
                                  setUploadingKey(null);
                                }
                              }}
                            />
                            <label
                              htmlFor={inputId}
                              className={`btn btn_sm btn_navber mb-0 ${
                                uploadingKey === rowKey ? "disabled" : ""
                              }`}
                              style={{
                                padding: "4px 12px",
                                fontSize: 12,
                                cursor:
                                  uploadingKey === rowKey
                                    ? "wait"
                                    : "pointer",
                              }}
                            >
                              {uploadingKey === rowKey ? "…" : "Upload"}
                            </label>
                            {uploadNote[rowKey] && (
                              <div
                                className={`small mt-1 ${
                                  uploadNote[rowKey].includes("Saved")
                                    ? "text-success"
                                    : "text-danger"
                                }`}
                              >
                                {uploadNote[rowKey]}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ImageGuideArea;
